import { buildOpenGuidedStudioIntakeSearchParams } from '@/features/guidedStudio/intake/integration/guidedStudioIntakeRouting'

import { getToolAvailability, isToolStartAllowed } from '../config/toolMatrix'
import type {
  AccessState,
  BestNextMoveRecommendation,
  CommandDeckSurfaceMode,
  PrimarySurface,
  ToolId,
} from '../contracts/types'

export type CommandDeckContinueKind = 'continue' | 'fallback' | 'hold' | 'preview_blocked'

export type CommandDeckContinueResolution = {
  kind: CommandDeckContinueKind
  label: string
  targetPath: string | null
  navigable: boolean
  usesContinueLabel: boolean
  reason: string
}

export type ResolveCommandDeckContinueInput = {
  mode: CommandDeckSurfaceMode
  unlocked: boolean
  accessState: AccessState
  primary: BestNextMoveRecommendation
  lastActiveProjectId?: string
}

const LABEL_CONTINUE = 'Continue →'
const LABEL_OPEN_GUIDED_STUDIO = 'Open Guided Studio'
const LABEL_REVIEW_NEXT_MOVE = 'Review next move'

const SURFACE_PATH: Partial<Record<PrimarySurface, string>> = {
  guided_studio: '/guided-studio',
  omnistudio: '/omnistudio',
  omnicine: '/omnicine',
  omnicast: '/omnicast',
  scapes_worlds: '/guided/start-from-world',
  xr_worlds: '/wearables/connect',
}

const SURFACE_TOOL: Partial<Record<PrimarySurface, ToolId>> = {
  guided_studio: 'guided_studio',
  omnistudio: 'omnistudio',
  omnicine: 'omnicine',
  omnicast: 'omnicast',
  scapes_worlds: 'scapes_worlds',
  xr_worlds: 'xr_worlds',
}

const HOLD_CATEGORIES = new Set(['request_access', 'book_demo'])

function buildGuidedStudioIntakePath(): string {
  const search = buildOpenGuidedStudioIntakeSearchParams(new URLSearchParams(), 'idea_seed')
  return `/guided-studio?${search.toString()}`
}

function isPreviewSafeTarget(targetPath: string): boolean {
  const path = targetPath.split('?')[0] ?? targetPath
  if (
    path.startsWith('/omnistudio') ||
    path.startsWith('/omnicine') ||
    path.startsWith('/omnicast')
  ) {
    return false
  }
  return (
    path === '/guided-studio' ||
    path.startsWith('/guided-studio/') ||
    path === '/guided/start-from-world' ||
    path.startsWith('/guided/start-from-world/') ||
    path.startsWith('/command-deck')
  )
}

function prefersGuidedStudioIntake(primary: BestNextMoveRecommendation): boolean {
  return (
    primary.category === 'improve_structure' ||
    primary.category === 'continue_building' ||
    primary.suggestedStageId === 'signal_captured' ||
    primary.suggestedStageId === 'intent_framed'
  )
}

function resolveOperationalTargetPath(
  surface: PrimarySurface,
  accessState: AccessState,
  useIntake: boolean
): string | null {
  const toolId = SURFACE_TOOL[surface]
  if (!toolId) {
    return null
  }

  const availability = getToolAvailability(accessState, toolId)
  if (!isToolStartAllowed(availability)) {
    return null
  }

  if (surface === 'guided_studio' && useIntake) {
    return buildGuidedStudioIntakePath()
  }

  return SURFACE_PATH[surface] ?? null
}

function continueResolution(
  targetPath: string,
  reason: string
): CommandDeckContinueResolution {
  return {
    kind: 'continue',
    label: LABEL_CONTINUE,
    targetPath,
    navigable: true,
    usesContinueLabel: true,
    reason,
  }
}

function fallbackResolution(
  label: string,
  targetPath: string | null,
  navigable: boolean,
  reason: string,
  kind: CommandDeckContinueKind = 'fallback'
): CommandDeckContinueResolution {
  return {
    kind,
    label,
    targetPath,
    navigable,
    usesContinueLabel: false,
    reason,
  }
}

function applyPreviewGuard(
  mode: CommandDeckSurfaceMode,
  resolution: CommandDeckContinueResolution
): CommandDeckContinueResolution {
  if (
    mode !== 'preview' ||
    !resolution.targetPath ||
    isPreviewSafeTarget(resolution.targetPath)
  ) {
    return resolution
  }

  return fallbackResolution(
    LABEL_REVIEW_NEXT_MOVE,
    null,
    false,
    'preview_blocked_target',
    'preview_blocked'
  )
}

export function resolveCommandDeckContinue(
  input: ResolveCommandDeckContinueInput
): CommandDeckContinueResolution {
  const { mode, unlocked, accessState, primary } = input

  if (!unlocked) {
    return {
      kind: 'hold',
      label: '',
      targetPath: null,
      navigable: false,
      usesContinueLabel: false,
      reason: 'hold',
    }
  }

  if (
    HOLD_CATEGORIES.has(primary.category) ||
    primary.suggestedSurface === 'request_access' ||
    primary.suggestedSurface === 'book_demo'
  ) {
    return {
      kind: 'hold',
      label: '',
      targetPath: null,
      navigable: false,
      usesContinueLabel: false,
      reason: 'access_pathway',
    }
  }

  if (primary.category === 'pathway_education' || primary.suggestedSurface === 'none') {
    return fallbackResolution(LABEL_REVIEW_NEXT_MOVE, null, false, 'pathway_education')
  }

  const intakePreferred = prefersGuidedStudioIntake(primary)
  const surface = primary.suggestedSurface

  if (
    surface !== 'none' &&
    surface !== 'request_access' &&
    surface !== 'book_demo' &&
    surface !== 'rights_passport'
  ) {
    const targetPath = resolveOperationalTargetPath(
      surface,
      accessState,
      surface === 'guided_studio' && intakePreferred
    )

    if (targetPath) {
      return applyPreviewGuard(
        mode,
        continueResolution(targetPath, 'operational_suggested_surface')
      )
    }
  }

  if (isToolStartAllowed(getToolAvailability(accessState, 'guided_studio'))) {
    const gsPath = intakePreferred ? buildGuidedStudioIntakePath() : '/guided-studio'

    if (primary.suggestedSurface === 'guided_studio') {
      return applyPreviewGuard(mode, continueResolution(gsPath, 'guided_studio_default'))
    }

    return applyPreviewGuard(
      mode,
      fallbackResolution(LABEL_OPEN_GUIDED_STUDIO, gsPath, true, 'guided_studio_safe_fallback')
    )
  }

  return fallbackResolution(LABEL_REVIEW_NEXT_MOVE, null, false, 'no_resolved_route')
}
